import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Card, Table, Badge, Button, Select, Spinner, QueryError } from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import { useToast } from "../contexts/ToastContext";

interface Notification {
  id: string;
  tenantId: string | null;
  type: string;
  channel: string;
  subject: string | null;
  body: string | null;
  status: string;
  createdAt: string;
  sentAt: string | null;
  readAt: string | null;
}

const STATUS_COLOR: Record<string, "green" | "gray" | "red" | "blue"> = {
  sent: "green",
  read: "gray",
  failed: "red",
  pending: "blue",
};

export default function Notifications() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data, isLoading, error } = useQuery<{ notifications: Notification[]; total: number }>({
    queryKey: ["notifications-all"],
    queryFn: () => apiGet("/api/notifications?limit=100"),
  });

  usePolling([["notifications-all"]], 30_000);

  const markRead = useMutation({
    mutationFn: (id: string) => apiPost(`/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiPost("/api/notifications/mark-all-read"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
      toast.success("All notifications marked as read");
    },
    onError: (err: Error) => toast.error("Failed to mark all read", err.message),
  });

  const dismissNotification = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-recent"] });
      toast.success("Notification dismissed");
    },
    onError: (err: Error) => toast.error("Failed to dismiss notification", err.message),
  });

  const notifications = data?.notifications ?? [];

  // Derive unique types for filter dropdown
  const typeOptions = useMemo(() => {
    const types = new Set(notifications.map((n) => n.type));
    return [
      { value: "all", label: "All types" },
      ...Array.from(types)
        .sort()
        .map((t) => ({ value: t, label: t.replace(/_/g, " ") })),
    ];
  }, [notifications]);

  // Apply filters
  const filtered = useMemo(() => {
    let result = notifications;
    if (typeFilter !== "all") {
      result = result.filter((n) => n.type === typeFilter);
    }
    if (statusFilter === "unread") {
      result = result.filter((n) => !n.readAt);
    } else if (statusFilter === "read") {
      result = result.filter((n) => !!n.readAt);
    }
    return result;
  }, [notifications, typeFilter, statusFilter]);

  const unreadCount = notifications.filter((n) => !n.readAt).length;

  const columns = [
    {
      key: "subject",
      header: "Subject",
      render: (row: Notification) => (
        <span style={{ fontWeight: row.readAt ? 400 : 600, color: "var(--c-text)" }}>
          {row.subject ?? row.type}
        </span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row: Notification) => <Badge color="gray">{row.type.replace(/_/g, " ")}</Badge>,
    },
    {
      key: "status",
      header: "Status",
      render: (row: Notification) => (
        <Badge color={STATUS_COLOR[row.status] ?? "gray"} dot>
          {row.status}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Date",
      render: (row: Notification) => (
        <span className="font-mono text-xs">{new Date(row.createdAt).toLocaleString()}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row: Notification) => (
        <div className="flex gap-1">
          {!row.readAt && (
            <Button size="sm" variant="ghost" onClick={() => markRead.mutate(row.id)}>
              Mark read
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => dismissNotification.mutate(row.id)}
            disabled={dismissNotification.isPending}
          >
            Dismiss
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Notifications" subtitle="Platform notifications and alerts" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Notifications" subtitle="Platform notifications and alerts" />
        <QueryError error={error} queryKeys={[["notifications-all"]]} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={`${data?.total ?? 0} notifications`}
        actions={
          unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              {markAllRead.isPending ? "Marking..." : `Mark all read (${unreadCount})`}
            </Button>
          ) : undefined
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="w-48">
          <Select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={typeOptions}
          />
        </div>
        <div className="w-44">
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "all", label: "All" },
              { value: "unread", label: "Unread" },
              { value: "read", label: "Read" },
            ]}
          />
        </div>
      </div>

      <Card padding={false}>
        <Table
          columns={columns}
          data={filtered as unknown as Record<string, unknown>[]}
          keyField="id"
          emptyMessage="No notifications match the current filters"
        />
      </Card>
    </div>
  );
}
